import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead, { injectSchema } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { featuredComics, categories } from '@/mocks/comics';

const sortOptions = [
  { value: 'latest', label: '最新更新' },
  { value: 'popular', label: '最受欢迎' },
  { value: 'rating', label: '评分最高' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('latest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const comics = useMemo(() => {
    if (!slug) return [];
    if (slug === 'all') return featuredComics;
    return featuredComics.filter((c) => c.category === slug);
  }, [slug]);

  const sortedComics = useMemo(() => {
    const list = [...comics];
    if (sortBy === 'popular') {
      return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    if (sortBy === 'rating') {
      return list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [comics, sortBy]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  useEffect(() => {
    if (!category) return;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name} - 虚空韩漫网`,
      description: `虚空韩漫网${category.name}分类，提供${category.count}部精选韩国漫画在线阅读，无遮挡下拉式阅读体验。`,
      url: `https://xukonghanman.com/category/${slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '虚空韩漫网',
      },
    };
    injectSchema(schema);
  }, [category, slug]);

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到" description="抱歉，您访问的分类页面不存在。" keywords="虚空韩漫网" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900">分类未找到</h1>
            <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name}分类`}
        description={`虚空韩漫网${category.name}分类，提供${category.count}部精选韩国漫画在线阅读。无遮挡下拉式韩漫阅读体验，每日更新热门${category.name}韩漫漫画。`}
        keywords={`虚空韩漫网,${category.name},韩漫漫画,韩国漫画在线阅读,无遮挡下拉式韩漫`}
        canonicalUrl={`https://xukonghanman.com/category/${slug}`}
        ogType="website"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-20">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                category.color === 'primary'
                  ? 'bg-primary-100 text-primary-500'
                  : category.color === 'accent'
                  ? 'bg-accent-100 text-accent-600'
                  : 'bg-secondary-100 text-secondary-500'
              }`}>
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl font-heading font-semibold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">共 {category.count} 部作品</p>
              </div>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 cursor-pointer ${
                    viewMode === 'grid' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                  aria-label="网格视图"
                >
                  <i className="ri-layout-grid-line"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 cursor-pointer ${
                    viewMode === 'list' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                  aria-label="列表视图"
                >
                  <i className="ri-list-check"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-[200px] flex-shrink-0">
              <div className="sticky top-24">
                <h4 className="text-sm font-label font-semibold text-foreground-900 mb-3 uppercase tracking-wide">
                  全部分类
                </h4>
                <nav className="space-y-0.5">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-150 whitespace-nowrap ${
                        cat.slug === slug
                          ? 'bg-primary-100 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                      <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {sortedComics.map((comic) => (
                    <div key={comic.slug}>
                      <Link
                        to={`/comic/${comic.slug}`}
                        className="group cursor-pointer block"
                      >
                        <div className="relative rounded-lg overflow-hidden aspect-[3/4] bg-background-100">
                          <img
                            src={comic.cover}
                            alt={comic.title}
                            title={`${comic.title} - 虚空韩漫网`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {comic.isHot && (
                            <div className="absolute top-2 left-2">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-label font-semibold bg-primary-500 text-white">HOT</span>
                            </div>
                          )}
                          {comic.rating && (
                            <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 text-white text-xs">
                              <i className="ri-star-fill text-secondary-400 text-[10px]"></i>
                              {comic.rating}
                            </div>
                          )}
                        </div>
                        <h3 className="mt-3 font-medium text-foreground-900 text-sm group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">
                          {comic.chapters}话 · {comic.status}
                        </p>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {sortedComics.map((comic) => (
                    <Link
                      key={comic.slug}
                      to={`/comic/${comic.slug}`}
                      className="flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer group"
                    >
                      <div className="w-[72px] h-[96px] rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={comic.cover} alt={comic.title} title={comic.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <h3 className="font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                            {comic.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                          <p className="text-xs text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
                            {comic.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="flex items-center gap-1 text-xs text-secondary-500">
                            <i className="ri-star-fill text-xs"></i>
                            {comic.rating}
                          </span>
                          <span className="text-xs text-foreground-500">{comic.chapters}话</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'
                          }`}>
                            {comic.status}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {sortedComics.length === 0 && (
                <div className="text-center py-20">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                  <p className="mt-4 text-foreground-500">该分类下暂无漫画作品</p>
                  <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}